import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, ExternalLink, ShieldCheck, Award } from 'lucide-react';
import SEO from '../../components/SEO';
import StructuredData from '../../components/StructuredData';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';

const SITE = 'https://staymii.com';
const PAGE = '/press/staymii-mysti-certified';
const DATE_ISO = '2026-06-30';
const CERTIFICATE_IMAGE = 'https://customer-assets.emergentagent.com/job_3d0ffcd4-4cb9-461f-8576-a4b0a79df5ee/artifacts/tjr1p7te_StayMii-MySTIcertification.png';
const OG_BANNER = SITE + '/og/mysti-certified.jpg';
const MYSTI_LOGO = 'https://customer-assets.emergentagent.com/job_3d0ffcd4-4cb9-461f-8576-a4b0a79df5ee/artifacts/trvky2nh_MySTI%20Color%20Logo.png';

const CERT_NO = 'MOSTI/2026/STI202602151';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'NewsArticle',
  headline: 'StayMii Receives MySTI Certification from MOSTI for Locally Developed R&D Innovation',
  description:
    'StayMii — an Automated E-Invoicing & Hospitality Management Platform — has been awarded MySTI certification by Malaysia\u2019s Ministry of Science, Technology and Innovation (MOSTI), recognising it as a certified local R&D product effective 30 June 2026.',
  datePublished: DATE_ISO,
  dateModified: DATE_ISO,
  mainEntityOfPage: SITE + PAGE,
  image: [OG_BANNER, CERTIFICATE_IMAGE],
  author: { '@type': 'Organization', name: 'StayMii', url: SITE },
  publisher: {
    '@type': 'Organization',
    name: 'StayMii',
    url: SITE,
    logo: { '@type': 'ImageObject', url: SITE + '/og-image.jpg' },
  },
  keywords:
    'StayMii MySTI certification, MOSTI local R&D certification, Malaysia tourism technology, e-invoicing hospitality platform, MRANTI, local innovation certification',
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
    { '@type': 'ListItem', position: 2, name: 'Press', item: SITE + '/press' },
    { '@type': 'ListItem', position: 3, name: 'StayMii MySTI Certification', item: SITE + PAGE },
  ],
};

export default function MystiCertificationPressRelease() {
  return (
    <div className="min-h-screen bg-white" data-testid="mysti-press-release-page">
      <SEO
        title="StayMii Awarded MySTI Certification by MOSTI | Certified Local R&D Innovation"
        description="StayMii, an Automated E-Invoicing & Hospitality Management Platform, is now MySTI certified by Malaysia's Ministry of Science, Technology and Innovation (MOSTI) as a locally developed R&D product, effective 30 June 2026."
        keywords="StayMii MySTI certification, MOSTI certification Malaysia, local R&D product certification, MRANTI, Malaysian tourism technology, e-invoicing hospitality platform"
        canonicalUrl={PAGE}
        ogImage={OG_BANNER}
      />
      <StructuredData id="mysti-press" schema={[articleSchema, breadcrumbSchema]} />
      <LandingNavBar />

      <article className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        <Link to="/press" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1 mb-6" data-testid="back-to-press-link">
          <ArrowLeft className="w-3.5 h-3.5" /> All press releases
        </Link>

        <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 mb-4">
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> 30 June 2026</span>
          <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Kuala Lumpur, Malaysia</span>
          <span className="uppercase tracking-wider text-[10px] text-blue-700 font-semibold">For Immediate Release</span>
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6 leading-tight">
          StayMii&trade; Awarded MySTI Certification by Malaysia&rsquo;s Ministry of Science, Technology and Innovation (MOSTI), Recognising Its Locally Developed R&amp;D Innovation
        </h1>

        <figure className="mb-8 -mx-6 sm:mx-0">
          <img
            src="/og/mysti-certified.jpg"
            alt="StayMii is now MySTI Certified — a mark of trust and commitment to excellence in Malaysia's hospitality and tourism industry."
            className="w-full h-auto object-cover sm:rounded-xl shadow-sm border border-slate-200"
            loading="eager"
          />
          <figcaption className="text-xs text-slate-400 mt-2 px-6 sm:px-0">
            StayMii is officially MySTI Certified (Certificate No. {CERT_NO}).
          </figcaption>
        </figure>

        <div className="prose prose-slate max-w-none">
          <p className="text-lg leading-relaxed"><strong>Kuala Lumpur, Malaysia &ndash; 30 June 2026</strong> &ndash; StayMii&trade; is proud to announce that it has been awarded <strong>MySTI certification</strong> by the Ministry of Science, Technology and Innovation (MOSTI), officially recognising the platform as a certified locally developed research and development (R&amp;D) product and service, effective 30 June 2026.</p>

          <div className="not-prose my-8 rounded-xl border border-emerald-200 bg-emerald-50 p-5 flex flex-col sm:flex-row sm:items-center gap-5">
            <img src={MYSTI_LOGO} alt="MySTI certification mark" className="h-16 w-auto object-contain mx-auto sm:mx-0" data-testid="mysti-logo" />
            <div className="flex-1 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div>
                <p className="text-xs text-slate-500">Certified product</p>
                <p className="font-semibold text-slate-900">StayMii &mdash; Automated E-Invoicing &amp; Hospitality Management Platform</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Certificate no.</p>
                <p className="font-semibold text-slate-900">{CERT_NO}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Issued by</p>
                <p className="font-semibold text-slate-900">MOSTI &middot; Powered by MRANTI</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Validity</p>
                <p className="font-semibold text-slate-900">30 June 2026 &ndash; 30 June 2029</p>
              </div>
            </div>
          </div>

          <p>MySTI &mdash; the national certification programme (<em>Program Penggunaan Barangan dan Perkhidmatan Hasil R&amp;D Tempatan</em>) administered by MOSTI in collaboration with the Ministry of Finance (MOF) &mdash; recognises products and services that are genuinely developed through local research and development and have reached full commercial readiness. The certification serves as a national benchmark of quality, authenticity of local innovation, and readiness for market and government adoption.</p>

          <h2 className="text-xl font-bold text-slate-900 mt-10 mb-4">A National Endorsement of Malaysian-Built Technology</h2>
          <p>The MySTI certification affirms that StayMii is a home-grown Malaysian technology platform, engineered locally to serve the country&rsquo;s hospitality, tourism, and business communities. StayMii&rsquo;s certified solution spans automated LHDN-compliant e-invoicing, property and hospitality management, QR-Code e-ticketing, and digital market distribution &mdash; all developed to strengthen operational efficiency and digital competitiveness for Malaysian businesses.</p>

          <ul>
            <li>Verified as a genuine local R&amp;D product at full commercial readiness</li>
            <li>Priority recognition for government and enterprise procurement</li>
            <li>Enhanced market confidence and credibility for StayMii&rsquo;s customers and partners</li>
            <li>Alignment with Malaysia&rsquo;s national digital economy and innovation agenda</li>
          </ul>

          <blockquote className="border-l-4 border-emerald-500 pl-4 italic text-slate-700 my-8">
            &ldquo;Receiving MySTI certification is a proud milestone for StayMii and a validation of our commitment to building world-class technology right here in Malaysia. This national recognition reinforces our mission to help Malaysian hospitality and tourism businesses digitalise with confidence, using a platform proven to meet the highest standards of local innovation.&rdquo;
            <footer className="not-italic text-sm text-slate-500 mt-2">&mdash; A representative of StayMii</footer>
          </blockquote>

          <h2 className="text-xl font-bold text-slate-900 mt-10 mb-4">What MySTI Certification Means for Customers</h2>
          <p>For hotels, serviced apartments, museums, attractions, and short-stay operators, the MySTI mark provides added assurance that StayMii is a trusted, government-recognised local solution. It signals that the platform has been independently assessed for its innovation, quality, and commercial maturity &mdash; giving businesses greater confidence when choosing StayMii for e-invoicing, hospitality management, and digital distribution.</p>

          <div className="not-prose grid sm:grid-cols-2 gap-4 my-8">
            <div className="border border-slate-200 rounded-xl p-5 bg-slate-50 flex items-start gap-3">
              <ShieldCheck className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-slate-900 mb-1">Certified &amp; verifiable</p>
                <p className="text-sm text-slate-600">Authenticity can be verified via the official MySTI portal using certificate number {CERT_NO}.</p>
              </div>
            </div>
            <div className="border border-slate-200 rounded-xl p-5 bg-slate-50 flex items-start gap-3">
              <Award className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-slate-900 mb-1">Valid for three years</p>
                <p className="text-sm text-slate-600">Certification is effective from 30 June 2026 through 30 June 2029.</p>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 mt-12 not-prose">
            <div className="border border-slate-200 rounded-xl p-5 bg-blue-50">
              <h3 className="text-sm font-bold text-slate-900 mb-2">About StayMii</h3>
              <p className="text-sm text-slate-600 leading-relaxed">StayMii is a Malaysian-developed tourism and hospitality technology platform providing integrated digital solutions for hotels, serviced apartments, museums, attractions, and tourism businesses. Its solutions include LHDN-compliant e-invoicing, property and hospitality management, QR-Code e-ticketing and visitor management, and digital market distribution through the StayMii Global Market Connect (SGMC) Program.</p>
              <Link to="/sgmc" className="inline-flex items-center gap-1 text-xs text-blue-700 font-semibold mt-3 hover:underline" data-testid="sgmc-cta-inline">
                Explore SGMC <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
            <div className="border border-slate-200 rounded-xl p-5 bg-slate-50">
              <h3 className="text-sm font-bold text-slate-900 mb-2">About MySTI</h3>
              <p className="text-sm text-slate-600 leading-relaxed">MySTI (<em>Program Penggunaan Barangan dan Perkhidmatan Hasil R&amp;D Tempatan</em>) is a national certification programme administered by the Ministry of Science, Technology and Innovation (MOSTI) in collaboration with the Ministry of Finance (MOF), and powered by MRANTI. It certifies locally developed R&amp;D products and services that have achieved commercial readiness, promoting the adoption of Malaysian innovation.</p>
            </div>
          </div>

          <div className="mt-12 border-t pt-6 text-sm text-slate-500">
            <p><strong className="text-slate-700">Media enquiries:</strong> <a href="mailto:media@staymii.com" className="text-blue-600 hover:underline">media@staymii.com</a></p>
            <p><strong className="text-slate-700">Partnerships / SGMC onboarding:</strong> <a href="mailto:partners@staymii.com" className="text-blue-600 hover:underline">partners@staymii.com</a></p>
            <p className="mt-4 text-xs text-slate-400 leading-relaxed" data-testid="press-trademark-notice">
              <strong>Trademark Notice:</strong> StayMii&trade; is a trademark pending registration with the Intellectual Property Corporation of Malaysia (MyIPO). MySTI and the MySTI logo are marks of the Ministry of Science, Technology and Innovation (MOSTI), Malaysia.
            </p>
          </div>
        </div>
      </article>

      <LandingFooter />
    </div>
  );
}
